import React, { useState } from 'react';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  IconButton, 
  Menu, 
  Container, 
  Button, 
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import TelelineLogo from '../../assets/images/teleline-logo.svg';
import TelelineLogoLight from '../../assets/images/teleline-logo-light.svg';
import { motion } from 'framer-motion';
import { useThemeMode } from '../../theme/ThemeModeContext';



const Header = () => {
  const { t } = useTranslation();
  const { mode, toggleMode } = useThemeMode();
  const [anchorElNav, setAnchorElNav] = useState(null);
  const themeLabel = mode === 'dark' ? t('navigation.lightMode') : t('navigation.darkMode');

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const pages = [
    { name: t('navigation.home'), path: '/' },
    { name: t('navigation.services'), path: '/services' },
    { name: t('navigation.caseStudies'), path: '/case-studies' },
    { name: t('navigation.clients'), path: '/clients' },
    { name: t('navigation.about'), path: '/about' },
    { name: t('navigation.contact'), path: '/contact' },
  ];

  return (
    <AppBar 
      position="fixed"
      sx={{
        backgroundColor: 'var(--header-bg)',
        backdropFilter: 'blur(10px)',
        backgroundImage: 'none',
        boxShadow: 'none',
        borderBottom: '1px solid var(--header-border)',
        zIndex: 1200,
        top: 0,
        left: 0,
        right: 0,
      }}
    >
        <Container maxWidth="xl">
          <Toolbar 
            disableGutters
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1,
            }}
          >
            {/* Logo (flex: 1, matching the right section, keeps the nav centred) */}
            <Box sx={{ flex: 1, display: 'flex' }}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  transition: 'opacity var(--transition-fast)',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                {/* White-text logo in dark mode */}
                <img 
                  src={mode === 'dark' ? TelelineLogoLight : TelelineLogo} 
                  alt="Teleline Logo" 
                  style={{
                    height: '44px',
                    width: 'auto',
                    display: 'block',
                  }}
                />
              </Box>
            </motion.div>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ flex: '0 0 auto', display: { xs: 'none', md: 'flex' } }}>
              {pages.map((page) => (
                <Button
                  key={page.name}
                  component={RouterLink}
                  to={page.path}
                  onClick={handleCloseNavMenu}
                  sx={{
                    color: 'var(--header-text)',
                    textTransform: 'none',
                    fontSize: '1.05rem',
                    fontWeight: 500,
                    px: 2,
                    py: 1,
                    position: 'relative',
                    transition: 'color var(--transition-fast)',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '0%',
                      height: '2px',
                      background: 'var(--color-blue)',
                      transition: 'width var(--transition-base)',
                    },
                    '&:hover': {
                      color: 'var(--color-blue)',
                    },
                    '&:hover::after': {
                      width: '100%',
                    },
                  }}
                >
                  {page.name}
                </Button>
              ))}
            </Box>

            {/* Right Section - Theme toggle + Language Switcher + Mobile Menu */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <IconButton
                onClick={toggleMode}
                aria-label={themeLabel}
                title={themeLabel}
                sx={{
                  color: 'var(--header-text)',
                  '&:hover': { backgroundColor: 'var(--hover-bg)' },
                }}
              >
                {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
              </IconButton>
              <LanguageSwitcher />
              
              {/* Mobile Menu Button */}
              <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size="large"
                  aria-label="menu"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  sx={{
                    color: 'var(--header-text)',
                    '&:hover': {
                      backgroundColor: 'var(--hover-bg)',
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiPaper-root': {
                      backgroundColor: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      boxShadow: 'var(--shadow-lg)',
                    },
                  }}
                >
                  {pages.map((page) => (
                    <MenuItem 
                      key={page.name} 
                      onClick={handleCloseNavMenu}
                      component={RouterLink}
                      to={page.path}
                      sx={{
                        color: 'var(--header-text)',
                        '&:hover': {
                          backgroundColor: 'rgba(13, 148, 136, 0.08)',
                          color: 'var(--color-blue)',
                        },
                      }}
                    >
                      {page.name}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Box>
          </Toolbar>
        </Container>
    </AppBar>
  );
};

export default Header;
